import { ArrowRight, KeyRound, Layers, LineChart, ShieldCheck, Zap } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import ThemeToggle from '../components/ThemeToggle';
import { Alert, Button, Field, Input } from '../components/ui';
import { ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';

const HIGHLIGHTS = [
  { icon: Layers, title: 'Drag-and-drop API builder', body: 'Compose triggers, queries, transforms and branches on a canvas. Publish to a real URL.' },
  { icon: LineChart, title: 'Dashboards from your API shape', body: 'Point at an endpoint and Forge proposes KPIs, charts and tables from its response.' },
  { icon: ShieldCheck, title: 'Roles that actually gate things', body: 'Owner, admin, editor and viewer - enforced in the UI, the API and the flow engine.' },
  { icon: Zap, title: 'Realtime everywhere', body: 'Live co-editing with presence, streaming run traces and self-refreshing dashboards.' },
];

export default function LoginPage() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from ?? '/';

  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      if (mode === 'login') {
        await login(email, password);
        toast.success('Welcome back');
      } else {
        await register(name, email, password);
        toast.success('Account created');
      }
      navigate(from, { replace: true });
    } catch (err) {
      const message =
        err instanceof ApiError
          ? [err.message, ...err.issues].join(' — ')
          : 'Something went wrong. Please try again.';
      setError(message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="relative grid min-h-screen grid-cols-1 bg-ink-950 lg:grid-cols-2">
      {/* Sign-in happens before any preference is loaded, so the toggle lives here too. */}
      <div className="absolute top-3 right-3 z-10">
        <ThemeToggle />
      </div>

      {/* Marketing rail - hidden on small screens so the form owns the viewport. */}
      <div className="relative hidden overflow-hidden border-r border-ink-850 lg:flex lg:flex-col lg:justify-between lg:p-12">
        <div
          className="pointer-events-none absolute inset-0 opacity-70"
          style={{
            background:
              'radial-gradient(680px circle at 15% 10%, rgba(99,102,241,0.16), transparent 55%), radial-gradient(520px circle at 85% 85%, rgba(16,185,129,0.12), transparent 55%)',
          }}
        />
        <div className="relative">
          <div className="flex items-center gap-2.5">
            <div className="flex size-9 items-center justify-center rounded-xl bg-brand-500 font-bold text-white">F</div>
            <span className="text-lg font-semibold tracking-tight">Forge</span>
          </div>
          <h1 className="mt-12 max-w-md text-3xl leading-tight font-semibold tracking-tight text-ink-100">
            Build APIs, dashboards and access control without leaving the browser.
          </h1>
          <p className="mt-4 max-w-md text-sm leading-relaxed text-ink-400">
            Self-hosted, single binary-ish, and yours. Point it at a VPS, attach your domain, and ship
            internal tools in an afternoon.
          </p>

          <ul className="mt-12 space-y-6">
            {HIGHLIGHTS.map(({ icon: Icon, title, body }) => (
              <li key={title} className="flex gap-3.5">
                <div className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-lg border border-ink-800 bg-ink-900 text-brand-400">
                  <Icon className="size-4" />
                </div>
                <div>
                  <p className="text-sm font-medium text-ink-100">{title}</p>
                  <p className="mt-0.5 max-w-sm text-xs leading-relaxed text-ink-400">{body}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
        <p className="relative text-xs text-ink-600">
          Built on React Flow, Fastify, Socket.IO, Recharts and SQLite.
        </p>
      </div>

      <div className="flex items-center justify-center px-4 sm:px-5 py-8 sm:py-12">
        <div className="w-full max-w-sm">
          <div className="mb-6 sm:mb-8 flex items-center gap-2.5 lg:hidden">
            <div className="flex size-9 items-center justify-center rounded-xl bg-brand-500 font-bold text-white">F</div>
            <span className="text-lg font-semibold tracking-tight">Forge</span>
          </div>

          <h2 className="text-xl font-semibold tracking-tight text-ink-100">
            {mode === 'login' ? 'Sign in' : 'Create your account'}
          </h2>
          <p className="mt-1.5 text-sm text-ink-400">
            {mode === 'login'
              ? 'Use the account your administrator gave you.'
              : 'The first account on a fresh instance becomes the superadmin.'}
          </p>

          <form onSubmit={onSubmit} className="mt-7 space-y-4">
            {mode === 'register' && (
              <Field label="Name" required>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Ada Lovelace"
                  autoComplete="name"
                  required
                />
              </Field>
            )}

            <Field label="Email" required>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                autoComplete="email"
                autoFocus
                required
              />
            </Field>

            <Field
              label="Password"
              required
              hint={mode === 'register' ? 'At least 8 characters.' : undefined}
            >
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                minLength={8}
                required
              />
            </Field>

            {error && <Alert tone="danger">{error}</Alert>}

            <Button
              type="submit"
              variant="primary"
              size="lg"
              loading={busy}
              className="w-full"
              icon={busy ? undefined : <KeyRound className="size-4" />}
            >
              {mode === 'login' ? 'Sign in' : 'Create account'}
            </Button>
          </form>

          <button
            type="button"
            onClick={() => {
              setMode(mode === 'login' ? 'register' : 'login');
              setError(null);
            }}
            className="mt-5 flex w-full items-center justify-center gap-1.5 text-xs text-ink-400 transition-colors hover:text-ink-200"
          >
            {mode === 'login' ? "Don't have an account? Sign up" : 'Already have an account? Sign in'}
            <ArrowRight className="size-3" />
          </button>

          {import.meta.env.DEV && (
            <div className="mt-8 rounded-lg border border-ink-800 bg-ink-900 p-3.5">
              <p className="mb-2 text-[11px] font-medium tracking-wide text-ink-400 uppercase">Demo accounts</p>
              <div className="space-y-1.5">
                {[
                  ['owner', 'admin@example.com', 'admin12345'],
                  ['editor', 'editor@example.com', 'editor12345'],
                  ['viewer', 'viewer@example.com', 'viewer12345'],
                ].map(([role, demoEmail, demoPassword]) => (
                  <button
                    key={demoEmail}
                    type="button"
                    onClick={() => {
                      setMode('login');
                      setEmail(demoEmail);
                      setPassword(demoPassword);
                    }}
                    className="flex w-full items-center justify-between rounded-md px-2 py-1 text-left text-xs transition-colors hover:bg-ink-850"
                  >
                    <span className="font-mono text-ink-300">{demoEmail}</span>
                    <span className="text-[10px] text-ink-500 uppercase">{role}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
